import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject, takeUntil } from 'rxjs';

import { coreSelectors } from 'app/core/state';
import { formatUserName } from 'app/core/utilities/user-util';
import { VideoChatService } from 'app/modules/social/services/videochat.service';
import { SocialPageService } from 'app/modules/social/social-page/services/social-page.service';
import { RoomSizeInfo } from 'app/modules/social/types/room';
import { Interval } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import Playlist from 'entity/Playlist';
import Social from 'entity/Social';
import SocialTheme from 'entity/SocialTheme';
import User from 'entity/User';

import { ScootaversePageService } from '../../services/scootaverse-page.service';

export enum SavedRoomMenuAction {
  DELETE_ROOM = 'deleteRoom',
  EDIT_ROOM = 'editRoom',
  SHARE_LINK = 'shareLink',
  DUPLICATE_ROOM = 'duplicateRoom',
}

@Component({
  selector: 'room-info-preview',
  templateUrl: './room-info-preview.component.html',
  styleUrls: ['./room-info-preview.component.scss'],
  standalone: false,
})
export class RoomInfoPreviewComponent extends Loggable implements OnInit, OnDestroy {
  private destroyed$ = new ReplaySubject<boolean>(1);

  @Input() public roomName?: string;
  @Input() public playlistId?: string;
  @Input() public theme?: SocialTheme;
  @Input() public roomSizeInfo?: RoomSizeInfo;

  @Input() public isRoomListPage = false;
  @Input() public room?: Social;
  @Input() public scootaverseId?: string;
  @Output() public enterRoom = new EventEmitter<string>();
  @Output() public loadingEnterRoom = new EventEmitter<boolean>();
  @Output() public menuAction = new EventEmitter<SavedRoomMenuAction>();

  public user?: User;
  protected playlist?: Playlist;

  public checkBackgroundLoadingInterval?: Interval;

  public constructor(
    private store: Store,
    private videoChatService: VideoChatService,
    private scootaversePageService: ScootaversePageService,
    private socialPageService: SocialPageService,
  ) {
    super();
  }

  public async ngOnInit(): Promise<void> {
    this.store
      .select(coreSelectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ user }) => {
        this.user = user;
      });

    this.playlist = await this.videoChatService.getPlaylistById(this.playlistId);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public onEnterRoom(): void {
    if (this.room?.socialId) {
      this.loadingEnterRoom.emit(true);
      this.socialPageService.onEnterRoom(this.room).catch((e) => {
        this.loadingEnterRoom.emit(false);
        this.error('Error redirecting to room - ', e);
      });
      this.enterRoom.emit(this.room.socialId);
    }
  }

  public onShareLink(room?: Social): void {
    if (!this.user) {
      this.error('onShareLink: user is undefined');
      return;
    }
    if (room) {
      this.menuAction.emit(SavedRoomMenuAction.SHARE_LINK);
      this.socialPageService.onShareLink({ room, user: this.user });
    }
  }

  public onEditRoom(room?: Social): void {
    if (room) {
      this.menuAction.emit(SavedRoomMenuAction.EDIT_ROOM);
      void this.scootaversePageService.onEditRoom(room, this.scootaverseId);
    }
  }

  public onDeleteRoom(room?: Social): void {
    if (room) {
      this.menuAction.emit(SavedRoomMenuAction.DELETE_ROOM);
      this.socialPageService.onDeleteRoom(room);
    }
  }

  public onDuplicateRoom(room?: Social): void {
    if (room) {
      this.menuAction.emit(SavedRoomMenuAction.DUPLICATE_ROOM);
      this.socialPageService.onDuplicateRoom(room);
    }
  }

  public formatUserName(): string {
    return this.user ? formatUserName(this.user) : '';
  }
}
